import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./AccessoryConfigurationForm.css";

function AccessoryConfigurationForm() {
    const { accessoryId, configId } = useParams();
    const isEdit = !!configId && configId !== "new";
    const navigate = useNavigate();

    const [accessory, setAccessory] = useState(null);
    const [parameters, setParameters] = useState([]);
    const [model, setModel] = useState("");
    const [values, setValues] = useState({}); // { parameterId: value }
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Dropdown values state
    const [dropdownOptions, setDropdownOptions] = useState({}); // { parameterId: [values] }
    const [showAddValueModal, setShowAddValueModal] = useState(false);
    const [addValueParamId, setAddValueParamId] = useState(null);
    const [newValueText, setNewValueText] = useState("");
    const [addingValue, setAddingValue] = useState(false);
    const [deletingValueId, setDeletingValueId] = useState(null);

    useEffect(() => {
        fetchData();
    }, [accessoryId, configId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Get accessory info + parameters
            const [accRes, cfgListRes] = await Promise.all([
                axios.get(`/accessories/${accessoryId}`),
                axios.get(`/accessories/${accessoryId}/configurations`),
            ]);

            setAccessory(accRes.data.data);
            const params = cfgListRes.data.data.parameters || [];
            setParameters(params);

            // Fetch dropdown options for Dropdown type parameters
            const dropdownParams = params.filter(p => p.input_type === "Dropdown");
            const optionsMap = {};
            await Promise.all(
                dropdownParams.map(async (param) => {
                    try {
                        const res = await axios.get(`/accessories/parameters/${param.id}/values`);
                        optionsMap[param.id] = res.data.data || [];
                    } catch (err) {
                        optionsMap[param.id] = [];
                    }
                })
            );
            setDropdownOptions(optionsMap);

            // If editing, pre-fill model and values
            if (isEdit) {
                const singleRes = await axios.get(
                    `/accessories/${accessoryId}/configurations/${configId}`
                );
                const config = singleRes.data.data;
                setModel(config.model || "");

                // Convert values map: { parameterId: value }
                const preValues = {};
                params.forEach((p) => {
                    preValues[p.id] = config.values?.[p.id] ?? "";
                });
                setValues(preValues);
            } else {
                // Initialize empty values
                const emptyValues = {};
                params.forEach((p) => {
                    emptyValues[p.id] = "";
                });
                setValues(emptyValues);
            }
        } catch (error) {
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to load data",
            });
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: "", text: "" }), 6000);
    };

    // Helper function to identify optional fields
    const isAirSupplyPressure = (paramName) => {
        return paramName.toUpperCase().includes("AIR SUPPLY PRESSURE");
    };

    const handleValueChange = (parameterId, value) => {
        setValues((prev) => ({ ...prev, [parameterId]: value }));
    };

    // Functions for adding dropdown values
    const openAddValueModal = (paramId) => {
        setAddValueParamId(paramId);
        setNewValueText("");
        setShowAddValueModal(true);
    };

    const closeAddValueModal = () => {
        setShowAddValueModal(false);
        setAddValueParamId(null);
        setNewValueText("");
    };

    const handleAddNewValue = async () => {
        if (!newValueText.trim() || !addValueParamId) return;
        setAddingValue(true);
        try {
            await axios.post(`/accessories/parameters/${addValueParamId}/values`, {
                value_text: newValueText.trim(),
            });
            // Refresh dropdown options for this parameter
            const res = await axios.get(`/accessories/parameters/${addValueParamId}/values`);
            setDropdownOptions(prev => ({
                ...prev,
                [addValueParamId]: res.data.data || []
            }));
            setNewValueText("");
            showMessage("success", "Value added successfully");
        } catch (error) {
            showMessage("error", error.response?.data?.message || "Failed to add value");
        } finally {
            setAddingValue(false);
        }
    };

    const handleDeleteValue = async (valueId) => {
        setDeletingValueId(valueId);
        try {
            await axios.delete(`/accessories/parameters/values/${valueId}`);
            // Refresh dropdown options for this parameter
            const res = await axios.get(`/accessories/parameters/${addValueParamId}/values`);
            setDropdownOptions(prev => ({
                ...prev,
                [addValueParamId]: res.data.data || []
            }));
            showMessage("success", "Value deleted successfully");
        } catch (error) {
            showMessage("error", error.response?.data?.message || "Failed to delete value");
        } finally {
            setDeletingValueId(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!model.trim()) {
            showMessage("error", "Model / Selector is required.");
            return;
        }

        // Validate all required fields (except AIR SUPPLY PRESSURE)
        for (const param of parameters) {
            if (!isAirSupplyPressure(param.name)) {
                const value = values[param.id];
                if (!value || (typeof value === 'string' && !value.trim())) {
                    showMessage("error", `${param.name} is required. Please fill all fields.`);
                    return;
                }
            }
        }

        setSaving(true);
        try {
            const payload = { model: model.trim(), values };

            if (isEdit) {
                await axios.put(
                    `/accessories/${accessoryId}/configurations/${configId}`,
                    payload
                );
                showMessage("success", "Configuration updated successfully");
            } else {
                await axios.post(`/accessories/${accessoryId}/configurations`, payload);
                showMessage("success", "Configuration created successfully");
            }

            // Navigate back to Screen 2 after short delay
            setTimeout(() => {
                navigate(`/admin/accessory-component/${accessoryId}/configurations`);
            }, 1200);
        } catch (error) {
            showMessage("error", error.response?.data?.message || "Error saving configuration");
        } finally {
            setSaving(false);
        }
    };

    const renderInput = (param) => {
        const isRequired = !isAirSupplyPressure(param.name);
        const commonProps = {
            id: `param-${param.id}`,
            value: values[param.id] ?? "",
            onChange: (e) => handleValueChange(param.id, e.target.value),
            className: "form-input",
            required: isRequired,
        };

        if (param.input_type === "Number") {
            return <input type="number" step="any" {...commonProps} />;
        }

        if (param.input_type === "Dropdown") {
            const options = dropdownOptions[param.id] || [];
            return (
                <div className="dropdown-with-manage">
                    <select
                        id={`param-${param.id}`}
                        className="form-select"
                        value={values[param.id] ?? ""}
                        onChange={(e) => handleValueChange(param.id, e.target.value)}
                        required={isRequired}
                    >
                        <option value="">-- Select {param.name} --</option>
                        {options.map((opt) => (
                            <option key={opt.id} value={opt.value_text}>
                                {opt.value_text}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="btn-manage-dropdown"
                        onClick={() => openAddValueModal(param.id)}
                        title="Manage dropdown values"
                    >
                        ⚙
                    </button>
                </div>
            );
        }

        // For "Text" — render as text input
        return <input type="text" {...commonProps} placeholder={`Enter ${param.name}`} />;
    };

    if (loading) {
        return (
            <div className="acc-form-container">
                <div className="loading-container">Loading...</div>
            </div>
        );
    }

    return (
        <div className="acc-form-container">
            <div className="acc-form-header">
                <button
                    className="btn-back"
                    onClick={() =>
                        navigate(`/admin/accessory-component/${accessoryId}/configurations`)
                    }
                >
                    ← Back to Configurations
                </button>
                <nav className="breadcrumb">
                    Accessory Component Master &gt; {accessory?.name} &gt;{" "}
                    {isEdit ? "Edit Configuration" : "Add New Configuration"}
                </nav>
                <h1 className="acc-form-title">
                    {isEdit ? "Edit Configuration" : "Add New Configuration"}
                </h1>
                <p className="acc-form-subtitle">
                    {accessory?.name} — {isEdit ? `Editing model: ${model}` : "Define a new model and its parameter values"}
                </p>
            </div>

            {message.text && (
                <div className={`alert-message alert-${message.type}`}>{message.text}</div>
            )}

            <div className="form-card">
                <form onSubmit={handleSubmit}>
                    {/* Model / Selector - always first */}
                    <div className="form-section">
                        <div className="section-label">Model / Selector</div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="model-input">
                                Model / Selector{" "}
                                <span className="required-mark">*</span>
                                <span className="field-hint">
                                    (The dropdown value the user sees when filling the form)
                                </span>
                            </label>
                            <input
                                id="model-input"
                                type="text"
                                className="form-input model-input"
                                value={model}
                                onChange={(e) => setModel(e.target.value)}
                                placeholder="e.g. IPCV050S"
                                required
                                maxLength={150}
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Dynamic parameter fields */}
                    {parameters.length > 0 ? (
                        <div className="form-section">
                            <div className="section-label">
                                Parameter Values
                                <span className="section-hint">
                                    {" "}({parameters.length} parameters from Accessories Configuration Master)
                                </span>
                            </div>
                            <div className="params-grid">
                                {parameters.map((param) => (
                                    <div className="form-group" key={param.id}>
                                        <label className="form-label" htmlFor={`param-${param.id}`}>
                                            {param.name}
                                            {!isAirSupplyPressure(param.name) && (
                                                <span className="required-mark">*</span>
                                            )}
                                            <span className={`type-tag type-${param.input_type.toLowerCase()}`}>
                                                {param.input_type}
                                            </span>
                                        </label>
                                        {renderInput(param)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="no-params-info">
                            ℹ️ No parameters defined for <strong>{accessory?.name}</strong>. Only the Model field will be saved.{" "}
                            <button
                                type="button"
                                className="link-btn"
                                onClick={() =>
                                    navigate(
                                        `/admin/accessories-config/accessories/${accessoryId}/parameters`
                                    )
                                }
                            >
                                Define parameters →
                            </button>
                        </div>
                    )}

                    {/* Form actions */}
                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() =>
                                navigate(`/admin/accessory-component/${accessoryId}/configurations`)
                            }
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-save"
                            disabled={saving}
                        >
                            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Configuration"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Add Dropdown Value Modal */}
            {showAddValueModal && addValueParamId && (
                <div className="modal-overlay" onClick={closeAddValueModal}>
                    <div className="add-value-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="add-value-modal-header">
                            <h3>Manage Dropdown Values</h3>
                            <button
                                className="btn-close-modal"
                                onClick={closeAddValueModal}
                                type="button"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Add New Value Section */}
                        <div className="add-value-modal-body">
                            <label className="form-label">
                                Add New Value
                            </label>
                            <div className="add-value-input-row">
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newValueText}
                                    onChange={(e) => setNewValueText(e.target.value)}
                                    onKeyPress={(e) => e.key === "Enter" && handleAddNewValue()}
                                    placeholder="Enter dropdown value..."
                                    maxLength={200}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    className="btn-add-inline"
                                    onClick={handleAddNewValue}
                                    disabled={!newValueText.trim() || addingValue}
                                >
                                    {addingValue ? "..." : "Add"}
                                </button>
                            </div>
                        </div>

                        {/* Existing Values List */}
                        <div className="values-list-section">
                            <label className="form-label values-list-label">
                                Existing Values ({(dropdownOptions[addValueParamId] || []).length})
                            </label>
                            <div className="values-list-container">
                                {(dropdownOptions[addValueParamId] || []).length === 0 ? (
                                    <div className="no-values-msg">No values added yet</div>
                                ) : (
                                    <ul className="values-list">
                                        {(dropdownOptions[addValueParamId] || []).map((val, index) => (
                                            <li key={val.id} className="value-item">
                                                <span className="value-number">{index + 1}.</span>
                                                <span className="value-text">{val.value_text}</span>
                                                <button
                                                    type="button"
                                                    className="btn-delete-value"
                                                    onClick={() => handleDeleteValue(val.id)}
                                                    disabled={deletingValueId === val.id}
                                                    title="Delete value"
                                                >
                                                    {deletingValueId === val.id ? "..." : "×"}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="add-value-modal-actions">
                            <button
                                type="button"
                                className="btn-done"
                                onClick={closeAddValueModal}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AccessoryConfigurationForm;
